/**
 * F23 (Wave 2C) — admin commission-rates list page.
 *
 * Route: `/commission-rates`
 * Permission: `commission_rate:read` (super_admin + finance + support).
 *
 * Paginated venue table with current rate + "Change rate" CTA. The CTA opens
 * `EditRateDialog` which submits via `adminCommissionApi.setVenueRate` —
 * gated server-side on `commission_rate:write` (super_admin + finance only;
 * support sees a 403). Each row links to the F11 admin venue detail page.
 *
 * Forward-only rate-change semantics (Decision 2) — the dialog shows a
 * banner so the admin understands existing records keep the prior rate.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Chip,
  EmptyState,
  Pagination,
  SearchInput,
  Skeleton,
  Table,
  type TableColumn,
  useToast,
  useDebouncedValue,
} from '@bravemove/ui-web';
import { adminCommissionApi, type AdminCommissionRateRow } from '../../api/commission';
import { translateApiError } from '../../auth/errors';
import { useAuth } from '../../auth/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { EditRateDialog } from '../../components/commission/EditRateDialog';
import {
  PLATFORM_DEFAULT_RATE_BASIS_POINTS,
  type SetVenueCommissionRateInput,
} from '@bravemove/types';

const PAGE_SIZE = 25;

function formatRate(rateBp: number): string {
  return `${(rateBp / 100).toFixed(2)}%`;
}

function formatRelative(iso: string | null, locale: string): string {
  if (!iso) return '—';
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return iso;
  const diffMs = target - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const absMin = Math.abs(diffMin);
    if (absMin < 60) return rtf.format(diffMin, 'minute');
    const diffHour = Math.round(diffMin / 60);
    if (Math.abs(diffHour) < 24) return rtf.format(diffHour, 'hour');
    const diffDay = Math.round(diffHour / 24);
    return rtf.format(diffDay, 'day');
  } catch {
    return iso;
  }
}

export function CommissionRatesPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();

  const canWrite = auth.adminUser?.permissions?.includes('commission_rate:write') ?? false;

  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  // F61 (A4) — search once typing pauses, not per keystroke.
  const debouncedQ = useDebouncedValue(q, 300);
  const [rows, setRows] = useState<readonly AdminCommissionRateRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editTarget, setEditTarget] = useState<AdminCommissionRateRow | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminCommissionApi.listRates({ q: debouncedQ, page, perPage: PAGE_SIZE });
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, page, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const onSearch = (value: string) => {
    setPage(1);
    setQ(value);
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = useMemo<readonly TableColumn<AdminCommissionRateRow>[]>(
    () => [
      {
        key: 'venue',
        header: t('admin:commission.table.rates.column.venue', { defaultValue: 'Venue' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <a
              className="text-[13px] text-ink font-semibold hover:underline"
              href={`/venues/${row.venueId}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/venues/${row.venueId}`);
              }}
              data-testid={`commission-rates-venue-link-${row.venueId}`}
            >
              {row.venueName}
            </a>
          </div>
        ),
      },
      {
        key: 'slug',
        header: t('admin:commission.table.rates.column.slug', { defaultValue: 'Slug' }),
        cell: (row) => <span className="font-mono text-[11.5px] text-ink-3">{row.venueSlug}</span>,
      },
      {
        key: 'rate',
        header: t('admin:commission.table.rates.column.rate', { defaultValue: 'Active rate' }),
        cell: (row) => (
          <span
            className="font-mono text-[13px] text-ink"
            data-testid={`commission-rates-rate-${row.venueId}`}
          >
            {/* CST-011 — activeRateBp is nullable (venue with zero rate rows);
                render an explicit dash instead of coercing null → '0.00%'. */}
            {row.activeRate.rateBasisPoints === null
              ? '—'
              : formatRate(row.activeRate.rateBasisPoints)}
          </span>
        ),
      },
      {
        key: 'lastChanged',
        header: t('admin:commission.table.rates.column.last_changed', {
          defaultValue: 'Last changed',
        }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">
            {formatRelative(row.lastChangedAt, locale)}
          </span>
        ),
      },
      {
        key: 'changedBy',
        header: t('admin:commission.table.rates.column.last_changed_by', {
          defaultValue: 'Changed by',
        }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">
            {row.activeRate.setByAdminDisplayName ?? '—'}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('admin:commission.table.rates.column.actions', { defaultValue: 'Actions' }),
        align: 'end',
        cell: (row) => (
          <div className="flex justify-end">
            {canWrite ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditTarget(row)}
                data-testid={`commission-rates-edit-${row.venueId}`}
              >
                {t('admin:commission.action.edit_rate', { defaultValue: 'Change rate' })}
              </Button>
            ) : (
              <Chip data-testid={`commission-rates-readonly-${row.venueId}`}>
                {/* CST-010 — dedicated key (the borrowed kpis.records key
                    rendered "Records" on the read-only chip). */}
                {t('admin:commission.readonly_chip', {
                  defaultValue: 'Read-only',
                })}
              </Chip>
            )}
          </div>
        ),
      },
    ],
    [t, locale, navigate, canWrite],
  );

  const onConfirmRateChange = async (input: SetVenueCommissionRateInput) => {
    if (!editTarget) return;
    // Let any error bubble up so EditRateDialog can surface
    // COMMISSION_RATE_NO_CHANGE inline.
    await adminCommissionApi.setVenueRate(editTarget.venueId, input);
    toast.push({
      tone: 'success',
      title: t('admin:commission.toast.rate_saved', { defaultValue: 'Commission rate updated.' }),
    });
    void fetchList();
  };

  return (
    <div className="space-y-6" data-testid="commission-rates-page">
      <PageHeader
        title={t('admin:commission.page.rates.title', { defaultValue: 'Commission rates' })}
        subtitle={t('admin:commission.page.rates.description', {
          defaultValue:
            'Per-venue commission percentages. Rate changes apply forward-only; past records keep their snapshotted rate.',
        })}
      />

      <div className="max-w-sm">
        <SearchInput
          value={q}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={t('admin:commission.page.rates.search_placeholder', {
            defaultValue: 'Search venues by name or slug',
          })}
          data-testid="commission-rates-search"
        />
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="commission-rates-loading">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('admin:commission.page.rates.empty', { defaultValue: 'No venues found.' })}
        />
      ) : (
        <>
          <Table
            data={rows as AdminCommissionRateRow[]}
            columns={columns as TableColumn<AdminCommissionRateRow>[]}
            rowKey={(row) => row.venueId}
          />
          {pageCount > 1 ? (
            <div className="flex justify-end">
              <Pagination
                page={page}
                pageCount={pageCount}
                onChange={setPage}
                ariaLabel={t('admin:commission.page.rates.title', {
                  defaultValue: 'Commission rates pagination',
                })}
              />
            </div>
          ) : null}
        </>
      )}

      {editTarget ? (
        <EditRateDialog
          open={editTarget !== null}
          venueId={editTarget.venueId}
          venueName={editTarget.venueName}
          // CST-011 — a venue with zero rate rows resolves to the platform
          // default (10%) server-side; prefill the dialog with the same value.
          currentRateBasisPoints={
            editTarget.activeRate.rateBasisPoints ?? PLATFORM_DEFAULT_RATE_BASIS_POINTS
          }
          onConfirm={onConfirmRateChange}
          onClose={() => setEditTarget(null)}
        />
      ) : null}
    </div>
  );
}

export default CommissionRatesPage;
